import { useState, type FormEvent } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import { settingsApi, type MfaSetupResponse } from '@/services/settings';
import { ApiClientError } from '@/services/api';

export interface MfaSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type SetupStep = 'scan' | 'verify' | 'recovery';

/**
 * Modal for MFA (TOTP) setup flow
 * 1. Display QR code for scanning
 * 2. Verify with 6-digit code
 * 3. Display recovery codes (one-time only)
 */
export default function MfaSetupModal({ isOpen, onClose, onSuccess }: MfaSetupModalProps) {
  const [step, setStep] = useState<SetupStep>('scan');
  const [setupData, setSetupData] = useState<MfaSetupResponse | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize MFA setup when modal opens
  const initializeSetup = async () => {
    if (setupData) return; // Already initialized

    setIsLoading(true);
    setError(null);

    try {
      const response = await settingsApi.setupMfa();
      setSetupData(response);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to initialize MFA setup. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Verify the 6-digit code
  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();

    if (verificationCode.length !== 6) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await settingsApi.verifyMfaSetup({ code: verificationCode });
      setRecoveryCodes(response.recoveryCodes);
      setStep('recovery');
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Invalid code. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Download recovery codes as text file
  const downloadRecoveryCodes = () => {
    const content = [
      'FlowIO Recovery Codes',
      '=====================',
      '',
      'Store these codes in a safe place. Each code can only be used once.',
      '',
      ...recoveryCodes.map((code, i) => `${i + 1}. ${code}`),
      '',
      `Generated: ${new Date().toISOString()}`,
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'flowio-recovery-codes.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Copy all recovery codes to clipboard
  const copyRecoveryCodes = async () => {
    const content = recoveryCodes.join('\n');
    await navigator.clipboard.writeText(content);
  };

  const handleClose = () => {
    // Only allow closing after showing recovery codes or before starting
    if (step === 'recovery') {
      onSuccess();
    }
    setStep('scan');
    setSetupData(null);
    setVerificationCode('');
    setRecoveryCodes([]);
    setError(null);
    onClose();
  };

  const handleDone = () => {
    onSuccess();
    handleClose();
  };

  if (!isOpen) return null;

  // Initialize setup when modal opens
  if (!setupData && !isLoading && !error) {
    initializeSetup();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mfa-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={step !== 'verify' ? handleClose : undefined}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h2 id="mfa-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
          {step === 'scan' && 'Set Up Two-Factor Authentication'}
          {step === 'verify' && 'Verify Your Authenticator'}
          {step === 'recovery' && 'Save Your Recovery Codes'}
        </h2>

        {error && (
          <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 1: Scan QR Code */}
        {step === 'scan' && (
          <div>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner size="lg" />
              </div>
            ) : setupData ? (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.)
                </p>

                {/* QR Code - SECURITY: Only allow data URLs to prevent XSS */}
                <div className="flex justify-center mb-4 p-4 bg-white border rounded-lg">
                  {setupData.qrCodeUrl?.startsWith('data:image/') ? (
                    <img
                      src={setupData.qrCodeUrl}
                      alt="Scan this QR code with your authenticator app"
                      className="w-48 h-48"
                    />
                  ) : (
                    <div className="w-48 h-48 flex items-center justify-center bg-gray-100 rounded">
                      <span className="text-gray-500 text-sm text-center px-4">
                        QR code unavailable. Please use the manual code below.
                      </span>
                    </div>
                  )}
                </div>

                {/* Manual entry option */}
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-1">
                    Cannot scan? Enter this code manually:
                  </p>
                  <code className="block bg-gray-100 p-2 rounded text-sm font-mono break-all select-all">
                    {setupData.secret}
                  </code>
                </div>

                <Button
                  onClick={() => setStep('verify')}
                  className="w-full"
                >
                  Continue
                </Button>
              </>
            ) : null}
          </div>
        )}

        {/* Step 2: Verify Code */}
        {step === 'verify' && (
          <form onSubmit={handleVerify}>
            <p className="text-sm text-gray-600 mb-4">
              Enter the 6-digit code from your authenticator app to verify setup.
            </p>

            <Input
              label="Verification code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              autoComplete="one-time-code"
              autoFocus
            />

            <div className="flex gap-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep('scan')}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                type="submit"
                disabled={verificationCode.length !== 6}
                isLoading={isLoading}
                className="flex-1"
              >
                Verify
              </Button>
            </div>
          </form>
        )}

        {/* Step 3: Recovery Codes */}
        {step === 'recovery' && (
          <div>
            <Alert variant="warning" className="mb-4">
              <strong>Important:</strong> These recovery codes will not be shown again. Save them now in a secure location.
            </Alert>

            <p className="text-sm text-gray-600 mb-4">
              If you lose access to your authenticator app, you can use one of these codes to sign in. Each code can only be used once.
            </p>

            {/* Recovery codes list */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-2">
                {recoveryCodes.map((code, index) => (
                  <code
                    key={index}
                    className="bg-white px-2 py-1 rounded text-sm font-mono text-center border"
                  >
                    {code}
                  </code>
                ))}
              </div>
            </div>

            <div className="flex gap-3 mb-6">
              <Button
                variant="outline"
                onClick={downloadRecoveryCodes}
                className="flex-1"
                leftIcon={
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                }
              >
                Download
              </Button>
              <Button
                variant="outline"
                onClick={copyRecoveryCodes}
                className="flex-1"
                leftIcon={
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                }
              >
                Copy All
              </Button>
            </div>

            <Button onClick={handleDone} className="w-full">
              I Have Saved My Codes
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
